import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, TrashIcon, Bars3Icon } from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminNavbar from '../components/AdminNavbar';

const SortableTableRow = ({ entry, onDelete, onInputChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    position: isDragging ? 'relative' : undefined,
    backgroundColor: isDragging ? 'white' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td className="align-top p-6">
        <div {...attributes} {...listeners} className="cursor-move">
          <Bars3Icon className="h-5 w-5 text-gray-400" />
        </div>
      </td>
      <td className="px-6 py-4 align-top">
        <input
          type="text"
          value={entry.url}
          onChange={(e) => onInputChange(entry.id, 'url', e.target.value)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="/example-page/"
        />
      </td>
      <td className="px-6 py-4 align-top">
        <input
          type="text"
          value={entry.seo_title}
          onChange={(e) => onInputChange(entry.id, 'seo_title', e.target.value)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          maxLength={60}
        />
        <p className="mt-1 text-xs text-gray-500">{entry.seo_title.length}/60 characters</p>
      </td>
      <td className="px-6 py-4 align-top">
        <textarea
          value={entry.seo_description}
          onChange={(e) => onInputChange(entry.id, 'seo_description', e.target.value)}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          rows="6"
          maxLength={160}
        />
        <p className="mt-1 text-xs text-gray-500">{entry.seo_description?.length || 0}/160 characters</p>
      </td>
      <td className="px-6 py-4 align-top text-right">
        <button
          onClick={() => onDelete(entry.id)}
          className="text-red-600 hover:text-red-900"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
};

const SeoAdminPage = () => {
  const [seoEntries, setSeoEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({
    url: '',
    seo_title: '',
    seo_description: '',
    display_order: 0
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchSeoEntries();
  }, []);

  const fetchSeoEntries = async () => {
    try {
      setError(null);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/seo`);
      // Ensure we have all required fields
      const formattedEntries = response.data.map(entry => ({
        id: entry.id,
        url: entry.url || '',
        seo_title: entry.seo_title || '',
        seo_description: entry.seo_description || '',
        display_order: entry.display_order || 0
      }));
      setSeoEntries(formattedEntries);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching SEO entries:', error);
      setError('Failed to load SEO entries');
      setIsLoading(false);
    }
  };

  const handleAddEntry = () => {
    setNewEntry({
      url: '',
      seo_title: '',
      seo_description: '',
      display_order: seoEntries.length + 1
    });
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/seo`, newEntry);
      setSeoEntries([...seoEntries, { ...response.data, isNew: true }]);
      setIsModalOpen(false);
      setSaveStatus('success');
    } catch (error) {
      console.error('Error adding SEO entry:', error);
      setSaveStatus('error');
    }
  };

  const handleDeleteEntry = async (id) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        // Only make API call if it's an existing entry (not a temp one)
        if (!id.toString().includes('temp')) {
          await axios.delete(`${process.env.REACT_APP_API_URL}/api/seo/${id}`);
        }
        setSeoEntries(seoEntries.filter(entry => entry.id !== id));
        setSaveStatus('success');
      } catch (error) {
        console.error('Error deleting entry:', error);
        setSaveStatus('error');
      }
    }
  };

  const handleInputChange = (id, field, value) => {
    setSeoEntries(seoEntries.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setSeoEntries((items) => {
        const oldIndex = items.findIndex((item) => item.id.toString() === active.id);
        const newIndex = items.findIndex((item) => item.id.toString() === over.id);
        
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Update display_order values
        const updatedItems = newItems.map((item, index) => ({
          ...item,
          display_order: index
        }));

        // Save the new order to the backend
        axios.post(`${process.env.REACT_APP_API_URL}/api/seo/reorder`, {
          entries: updatedItems.map(({ id, display_order }) => ({ id, display_order }))
        }).catch(error => {
          console.error('Error saving order:', error);
          setSaveStatus('error');
        });

        return updatedItems;
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveStatus(null);
      
      // Process each entry
      await Promise.all(
        seoEntries.map(async (entry) => {
          const payload = {
            url: entry.url,
            seo_title: entry.seo_title,
            seo_description: entry.seo_description,
            display_order: entry.display_order
          };

          if (entry.isNew) {
            // Create new entry
            await axios.post(`${process.env.REACT_APP_API_URL}/api/seo`, payload);
          } else {
            // Update existing entry
            await axios.put(`${process.env.REACT_APP_API_URL}/api/seo/${entry.id}`, payload);
          }
        })
      );
      
      setSaveStatus('success');
      // Refresh data from database
      await fetchSeoEntries();
    } catch (err) {
      console.error('Error saving SEO entries:', err);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (entryId, file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/seo/${entryId}/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setSeoEntries(entries =>
        entries.map(entry =>
          entry.id === entryId
            ? { ...entry, seo_image: response.data.image_url }
            : entry
        )
      );
    } catch (err) {
      console.error('Error uploading image:', err);
    }
  };

  if (isLoading) return <div className="flex justify-center items-center h-screen">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNavbar />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0 mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-0">SEO Management</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage SEO settings for different pages. Drag rows to reorder them.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleAddEntry}
              className="btn btn--secondary"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New Entry
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn btn--primary"
            >
              {isSaving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>

        {saveStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-md">
            Changes saved successfully!
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
            Error saving changes. Please try again.
          </div>
        )}

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SEO Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SEO Description</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <SortableContext
                  items={seoEntries.map(e => e.id.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  {seoEntries.map((entry) => (
                    <SortableTableRow
                      key={entry.id}
                      entry={entry}
                      onDelete={handleDeleteEntry}
                      onInputChange={handleInputChange}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        </div>

        {/* Add New Entry Modal */}
        {isModalOpen && (
          <div className="fixed z-10 inset-0">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center my-64 sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <form onSubmit={handleModalSubmit} className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Add New SEO Entry
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                        URL Path
                      </label>
                      <input
                        type="text"
                        id="url"
                        value={newEntry.url}
                        onChange={(e) => setNewEntry({ ...newEntry, url: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="seo_title" className="block text-sm font-medium text-gray-700">
                        SEO Title
                      </label>
                      <input
                        type="text"
                        id="seo_title"
                        value={newEntry.seo_title}
                        onChange={(e) => setNewEntry({ ...newEntry, seo_title: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="seo_description" className="block text-sm font-medium text-gray-700">
                        SEO Description
                      </label>
                      <textarea
                        id="seo_description"
                        value={newEntry.seo_description}
                        onChange={(e) => setNewEntry({ ...newEntry, seo_description: e.target.value })}
                        rows={3}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="btn btn--secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                    >
                      Add Entry
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeoAdminPage;